import { useEffect, useCallback } from 'react';

export interface PostMessageAction {
    action: 'login' | 'logout';
    token?: any;
}

interface UsePostMessageListenerProps {
    onLogin?: (data: PostMessageAction) => void;
    onLogout?: (data: PostMessageAction) => void;
    onMessage?: (data: PostMessageAction) => void;
}

export const usePostMessageListener = ({
    onLogin,
    onLogout,
    onMessage
}: UsePostMessageListenerProps = {}) => {

    const handleMessage = useCallback((event: MessageEvent) => {
        try {
            // Validate message structure
            if (!event.data || typeof event.data !== 'object') {
                return;
            }

            const { action, token } = event.data as PostMessageAction;

            // Only handle login/logout actions
            if (!action || !['login', 'logout'].includes(action)) {
                return;
            }

            const messageData: PostMessageAction = { action, token };

            // Call the general message handler if provided
            if (onMessage) {
                onMessage(messageData);
            }

            // Call specific handlers
            switch (action) {
                case 'login':
                    if (onLogin) {
                        onLogin(messageData);
                    }
                    break;
                case 'logout':
                    if (onLogout) {
                        onLogout(messageData);
                    }
                    break;
            }
        } catch (error) {
            console.error('Error handling postMessage:', error);
        }
    }, [onLogin, onLogout, onMessage]);

    useEffect(() => {
        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [handleMessage]);

    // Return utility functions for sending messages
    const sendLoginMessage = useCallback((token: any = {}) => {
        try {
            window.parent.postMessage({ action: "login", token }, "*");
        } catch (error) {
            console.error('Error sending login message:', error);
        }
    }, []);

    const sendLogoutMessage = useCallback((token: any = {}) => {
        try {
            window.parent.postMessage({ action: "logout", token }, "*");
        } catch (error) {
            console.error('Error sending logout message:', error);
        }
    }, []);

    return {
        sendLoginMessage,
        sendLogoutMessage
    };
}; 